import axios, { type AxiosInstance } from 'axios';
import {
  resolveCashierDiscountMode,
  type CashierDiscountMode,
  type PosCustomFeeCalc,
  type PosDiscountPreset,
  type ReceiptFieldFlags,
  type ThemeTokens,
} from '@mlm/shared';
import { useAuthStore, type AuthUser } from './auth-store';
import { DEFAULT_COIN_LABEL } from './coin-label';
import { queryClient } from './query-client';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Turn a thrown request error into a human message. Axios swallows the server
 * body behind a generic "Request failed with status code 4xx"; this digs out
 * the API's `{ error, message, issues }` so toasts/dialogs show the real reason.
 */
export function apiErrorMessage(e: unknown, fallback = 'Request failed'): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as
      | {
          error?: string;
          message?: string;
          issues?: Array<{ path?: (string | number)[]; message?: string }>;
        }
      | undefined;
    if (data?.issues?.length) {
      return data.issues
        .map((i) => `${(i.path ?? []).join('.') || 'value'}: ${i.message ?? 'invalid'}`)
        .join('; ');
    }
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (e.message) return e.message;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

let refreshInFlight: Promise<string | null> | null = null;
async function refreshTokenOnce(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await axios.post<{ accessToken: string }>(
          `${BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );
        return res.data.accessToken;
      } catch {
        return null;
      } finally {
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      }
    })();
  }
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !String(original.url ?? '').includes('/auth/refresh') &&
      !String(original.url ?? '').includes('/auth/login') &&
      // A wrong-current-password 401 from the change-password form is a real
      // rejection, not an expired token — don't refresh+replay it (that burns
      // the account's 10/hr attempts, two per typo).
      !String(original.url ?? '').includes('/me/password')
    ) {
      original._retried = true;
      const newToken = await refreshTokenOnce();
      if (newToken) {
        const user = useAuthStore.getState().user;
        if (user) useAuthStore.getState().setSession(newToken, user);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      useAuthStore.getState().clear();
      queryClient.clear();
    }

    // Permission drift after a deploy that SPLIT a perm (0162 turned
    // `consignment.write` into seven per-action codes). Perms are baked into
    // the access token at login, so a cashier already signed in when the
    // migration ran carries the retired code and 403s on every consignment
    // write until their token happens to refresh — mid-shift, with no
    // self-service remedy. Re-hydrate once and replay; if the role genuinely
    // lacks the perm the retry 403s again and the error surfaces normally.
    if (
      error.response?.status === 403 &&
      typeof error.response?.data?.missing === 'string' &&
      error.response.data.missing.startsWith('consignment.') &&
      original &&
      !original._permRetried
    ) {
      original._permRetried = true;
      if (await hydrateSessionFromRefresh()) {
        const token = useAuthStore.getState().token;
        if (token) {
          original.headers.Authorization = `Bearer ${token}`;
          return api.request(original);
        }
      }
    }
    return Promise.reject(error);
  },
);

type LoginResponse = {
  accessToken: string;
  user: Omit<AuthUser, 'branchId' | 'mustChangePassword'> & {
    scope?: { branchId?: string | null };
    mustChangePassword?: boolean;
  };
};

export async function login(args: {
  email: string;
  password: string;
  totp?: string;
}): Promise<{ accessToken: string; user: AuthUser }> {
  const res = await api.post<LoginResponse>('/auth/login', args);
  const u = res.data.user;
  return {
    accessToken: res.data.accessToken,
    user: {
      id: u.id,
      email: u.email,
      fullName: u.fullName,
      roles: u.roles,
      perms: u.perms,
      totpEnabled: u.totpEnabled,
      mustChangePassword: u.mustChangePassword ?? false,
      branchId: u.scope?.branchId ?? null,
    },
  };
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    /* ignore */
  }
  useAuthStore.getState().clear();
  // Drop every cached query so the next signed-in user can't see the
  // previous account's data flash before refetches complete.
  queryClient.clear();
}

/** Change the signed-in cashier's own password (requires the current one). */
export async function changePassword(args: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await api.post('/me/password', args);
}

/**
 * Restore an in-memory session from the refresh cookie on the device. Called
 * once at app boot — the auth store is memory-only, so a page refresh starts
 * with `token=null`; without this the cashier would always be bounced to the
 * login screen even when the refresh cookie is still valid.
 *
 * `/auth/refresh` only returns the token, so we follow up with `/me` to
 * re-hydrate roles/perms/branch. Returns `true` if restored, `false` if the
 * cookie was missing/expired and the user must log in again.
 */
export async function restoreSession(): Promise<boolean> {
  const ok = await hydrateSessionFromRefresh();
  if (!ok) {
    useAuthStore.getState().clear();
    queryClient.clear();
  }
  return ok;
}

/**
 * Refresh the access token AND re-hydrate roles/perms from `/me`, WITHOUT
 * signing the user out when it fails.
 *
 * `/auth/refresh` re-mints permissions from the database but returns only the
 * token, so a session that predates a permission change keeps the stale
 * `user.perms` that drives `useHasPerm` — this is the only way to pick new
 * perms up mid-shift. Non-destructive on purpose: the 403 recovery path below
 * calls it speculatively, and a genuinely-unauthorized cashier must NOT be
 * logged out just because they clicked something they aren't allowed to do.
 */
async function hydrateSessionFromRefresh(): Promise<boolean> {
  try {
    const refreshRes = await axios.post<{ accessToken: string }>(
      `${BASE_URL}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const accessToken = refreshRes.data.accessToken;
    const meRes = await axios.get<{
      id: string;
      email: string;
      fullName: string | null;
      roles: string[];
      perms: string[];
      totpEnabled: boolean;
      mustChangePassword?: boolean;
      branchId: string | null;
    }>(`${BASE_URL}/me`, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const u = meRes.data;
    useAuthStore.getState().setSession(accessToken, {
      id: u.id,
      email: u.email,
      fullName: u.fullName,
      roles: u.roles,
      perms: u.perms,
      totpEnabled: u.totpEnabled,
      // Re-hydrated on every boot: the flag must be read HERE too, not only in
      // the login mapper, or a page refresh drops the forced-change gate.
      mustChangePassword: u.mustChangePassword ?? false,
      branchId: u.branchId ?? null,
    });
    return true;
  } catch {
    return false;
  }
}

// The receipt shapes are shared (@mlm/shared) so the cashier tape and the admin
// drill-down render from ONE definition. Imported for local use AND re-exported,
// because this module is the cashier's single API-types surface.
import type { ReceiptHeader, PosReceiptItem } from '@mlm/shared';
export type { ReceiptHeader, PosReceiptItem };

export type Branch = {
  id: string;
  code: string;
  name: string;
  type: string;
  completionMode?: 'auto' | 'manual';
  receiptHeader?: ReceiptHeader;
  /** Which optional blocks the tape prints. Sparse — merge via `resolveReceiptFields`. */
  receiptFields?: Partial<ReceiptFieldFlags>;
  /** Thermal roll width in mm (58 or 80); resolve via `resolveReceiptPaperWidth`. */
  receiptPaperWidth?: number;
  // Per-branch order-number series prefix (e.g. "MB", "SO-"). The numeric
  // series is appended to this on `orders.code`; the UI strips it to show a
  // clean "Sales Order #0001".
  orderPrefix?: string | null;
  // Per-branch cashier-discount policy: the mode (off / free / presets_only)
  // and the caps a typed discount is held to. `cashierDiscountEnabled` is the
  // boolean the mode replaced; read both through `cashierDiscountFromBranch`.
  cashierDiscountMode?: CashierDiscountMode;
  cashierDiscountEnabled?: boolean;
  cashierDiscountMaxPct?: string;
  cashierDiscountMaxAmount?: string;
};

export async function listBranches(): Promise<Branch[]> {
  const res = await api.get<Branch[]>('/branches');
  return res.data;
}

export type KitCode = {
  id: string;
  code: string;
  pin: string;
  kitProductId: string;
  kitMembershipId: string;
};

/** A product variation sellable at the till (branch-scoped price + stock). */
export type PosVariant = {
  id: string;
  sku: string;
  name: string;
  attributes: Record<string, string>;
  /** Branch-effective cash price (branch variant override or variant base). */
  price: string;
  /** BF4: flat per-currency prices (null = not sold in that currency). */
  priceGc: string | null;
  priceCoin: string | null;
  pv: string;
  /** This variant's stock at the selected branch. */
  stockQty: number;
  inStock: boolean;
};

export type PosProduct = {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  basePrice: string;
  /** The customer-less tile price: always the base price (a price follows the
   *  buyer; `/pos/price-map` re-prices once a member is selected). */
  effectivePrice: string;
  /** BF4: flat per-currency prices (null = not sold in that currency). */
  priceGc: string | null;
  priceCoin: string | null;
  pv: string;
  images: string[];
  featured: boolean;
  /** The kit's (or plain product's) own branch stock. */
  qty: number;
  /**
   * Effective units sellable now: own stock, or — for a kit short on its own
   * stock — how many can be assembled from its inclusive items. At a STOCKIST
   * it is own stock plus what opening the bundles it holds would release. For a
   * variation product this is the SUM of in-branch variant stock. The till
   * gates add/disable/qty-cap on THIS, not `qty`.
   */
  sellableQty: number;
  isKit: boolean;
  /** Virtual product / service: not inventory-tracked, always sellable. */
  isVirtual: boolean;
  /** LMS course. Always also virtual — a course product must be virtual to be
   *  linked to a course at all. Selling it grants access to the SELECTED
   *  member, which is what the till has to warn about. */
  isCourse: boolean;
  /** True when `sellableQty` is only achievable via component assembly. */
  assembledFromComponents: boolean;
  /** The product's category, for the till's chips; null when it has none. */
  categoryId: string | null;
  categoryName: string | null;
  /** The admin's category order, so the chips follow the menu's order. */
  categorySortOrder: number | null;
  /** Active variations; [] for a single-SKU product. A variation product is
   *  sold per-variant (the picker gates add-to-cart on the chosen variant). */
  variants: PosVariant[];
};

/** One row of the append-only stock-movement ledger (Inventory → View log).
 *  Shape mirrors the admin SPA's `InventoryMovement` (cross-app imports are
 *  banned; the type duplicate is the sanctioned cost). */
export type InventoryMovement = {
  id: string;
  productId: string;
  productSku: string;
  productName: string;
  variantId: string | null;
  variantName: string | null;
  variantSku: string | null;
  /** Signed quantity change. */
  delta: number;
  reason: string;
  balanceBefore: number;
  balanceAfter: number;
  refType: string | null;
  refId: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  note: string | null;
  createdAt: string;
};

export async function listInventoryMovements(params: {
  branchId: string;
  productId?: string;
  variantId?: string;
  reason?: string;
  from?: string;
  to?: string;
  limit?: number;
}): Promise<InventoryMovement[]> {
  const res = await api.get<InventoryMovement[]>('/pos/inventory/movements', { params });
  return res.data;
}

export async function listPosProducts(branchId: string): Promise<PosProduct[]> {
  const res = await api.get<PosProduct[]>('/pos/products', {
    params: { branchId },
  });
  return res.data;
}

/** One recipe row: what a sold unit uses up. Names and quantities, never cost. */
export type PosRecipeLine = {
  /** Null = the product-level recipe; set = one variation's own rows. */
  variantId: string | null;
  materialProductId: string;
  name: string;
  unitLabel: string | null;
  qtyPerUnit: number;
};

/** A product's recipe, for the till's item sheet (read-only). */
export async function getPosProductRecipe(productId: string): Promise<PosRecipeLine[]> {
  const res = await api.get<{ materials: PosRecipeLine[] }>(`/pos/products/${productId}/recipe`);
  return res.data.materials;
}

/**
 * A material (cups, lids) and what the branch holds of it. Materials are used
 * up by recipes and never rung up, so they are not in `listPosProducts`.
 */
export type PosMaterial = {
  id: string;
  sku: string;
  name: string;
  /** Display unit ("pcs", "g", "ml"); null = pieces. */
  unitLabel: string | null;
  qty: number;
};

export async function listPosMaterials(branchId: string): Promise<PosMaterial[]> {
  const res = await api.get<PosMaterial[]>('/pos/materials', { params: { branchId } });
  return res.data;
}

/**
 * Where a product is kept at the branch (item locator) and when its stock next
 * expires (stock lots). Sparse: only products that have either come back, and
 * nothing at all while both tenant switches are off.
 */
export type PosShelfInfo = {
  productId: string;
  locationAisle: string | null;
  locationRack: string | null;
  /** `YYYY-MM-DD` of the soonest-expiring open lot, if any carries a date. */
  nearestExpiresOn: string | null;
  openLots: number;
};

export async function listPosShelfInfo(branchId: string): Promise<PosShelfInfo[]> {
  const res = await api.get<PosShelfInfo[]>('/pos/shelf-info', { params: { branchId } });
  return res.data;
}

/**
 * Per-slot discount overlay for the branch catalog. Keyed by product / variant
 * id, present only for entries the selected slot actually discounts below SRP
 * (membership / rank / stockist). The grid + cart apply this live over the base
 * catalog so switching slots re-prices instantly. Cash-only.
 */
export type PriceMapEntry = { unitPrice: string; appliedDiscountPct: string };
export type PosPriceMap = {
  products: Record<string, PriceMapEntry>;
  variants: Record<string, PriceMapEntry>;
};

/**
 * `buyerUserId` is required; `buyerSlotId` is optional. With no slot the server
 * still answers — a buyer who has never activated a slot may be priced at the
 * tenant's non-activated tier, and only the server can tell that apart from a
 * cashier deselecting an activated member's slot (which stays at SRP).
 */
export async function getPosPriceMap(
  branchId: string,
  buyerUserId: string,
  buyerSlotId: string | null,
  // "Sell to Stockist" mode: the stockist tier lives on the DESTINATION
  // branch's price rows, so the server has to know this isn't a retail sale or
  // the overlay comes back priced at the buyer's membership tier only.
  stockistTransfer = false,
): Promise<PosPriceMap> {
  const res = await api.get<PosPriceMap>('/pos/price-map', {
    params: {
      branchId,
      buyerUserId,
      ...(buyerSlotId ? { buyerSlotId } : {}),
      ...(stockistTransfer ? { stockistTransfer: 'true' } : {}),
    },
  });
  return res.data;
}

/**
 * QoL Slice 5a — smart search returns BOTH user-level matches (legacy shape)
 * and slot-level matches (carry slotId + slotCode so the cashier can pick
 * a slot directly without a follow-up modal).
 */
export type MemberSearchResult = {
  matchType: 'user' | 'slot';
  id: string;
  email: string;
  phone: string | null;
  fullName: string | null;
  referralCode: string | null;
  slotId: string | null;
  slotCode: string | null;
};

export async function searchMembers(q: string): Promise<MemberSearchResult[]> {
  const res = await api.get<MemberSearchResult[]>('/pos/members/search', {
    params: { q },
  });
  return res.data;
}

export type ActiveSlot = {
  id: string;
  slotCode: string;
  membershipName: string;
  activatedAt: string | null;
  /** Spendable balances, decimal strings ("1234.5000"). Display only — never
   *  do arithmetic on them; the server re-checks at checkout. Materialized and
   *  therefore eventually consistent, so a just-earned commission may trail. */
  available: string;
  availableGc: string;
  availableCoin: string;
};

export async function listMemberActiveSlots(userId: string): Promise<ActiveSlot[]> {
  const res = await api.get<ActiveSlot[]>(`/pos/users/${userId}/slots`);
  return res.data;
}

// The stockist branch a SLOT owns, if any. When set, "Sell to Stockist" mode
// transfers stock into that branch instead of ringing up a normal retail sale.
export type StockistBranch = { id: string; name: string };

export async function getSlotStockistBranch(slotId: string): Promise<StockistBranch | null> {
  const res = await api.get<StockistBranch | null>(`/pos/slots/${slotId}/stockist`);
  return res.data;
}

// Search stockists to sell to (for the Sell to Stockist tab). Results carry the
// owner SLOT (matchType 'slot'), so picking one binds that slot for the transfer.
export async function searchStockists(q: string): Promise<MemberSearchResult[]> {
  const res = await api.get<MemberSearchResult[]>('/pos/stockists/search', { params: { q } });
  return res.data;
}

export type PreviewLine = {
  productId: string;
  productName: string;
  productSku: string;
  qty: number;
  unitPrice: string;
  unitPriceBase: string;
  appliedDiscountPct: string;
  unitPv: string;
};

export type PreviewResult = {
  lines: PreviewLine[];
  subtotal: string;
  discountTotal: string;
  total: string;
  totalPv: string;
  currency: 'cash' | 'gc' | 'coin';
  // Slice 5b: order-level cashier discount, when applied.
  managerDiscountPct: string;
  managerDiscountAmount: string;
  // The preset pressed or the code typed, already taken off `total`. `couponLabel`
  // is a preset's name; null means show the code. Optional: an API older than
  // till coupons does not send them.
  couponCode?: string | null;
  couponTotal?: string;
  couponLabel?: string | null;
};

export async function previewCheckout(payload: {
  branchId: string;
  buyerUserId: string;
  buyerSlotId?: string | null;
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  paymentCurrency?: 'cash' | 'gc' | 'coin';
  // QoL Slice 5b: preview the manager discount alongside the rest so the
  // Review modal can update the post-override total as the cashier types.
  managerDiscount?: { pct?: number; amount?: number };
  /** The preset pressed, or the code typed. One or the other, never both. */
  discountPresetId?: string;
  couponCode?: string;
  /** A gift check of the buyer's, instead of either of the above. */
  voucherId?: string;
  // Must match what /pos/checkout is sent, or Review quotes membership pricing
  // and the Charge rings up the stockist price.
  stockistTransfer?: boolean;
}): Promise<PreviewResult> {
  const res = await api.post<PreviewResult>('/pos/checkout/preview', payload);
  return res.data;
}

/** F1 CMS: an admin-managed POS payment method the cashier renders as a tender button. */
export type PosPaymentMethodOption = {
  code: string;
  label: string;
  tenderKind: 'cash' | 'exact' | 'wallet';
  currency: 'cash' | 'gc' | 'coin';
  /**
   * The custom fees a cashier may add when paying with THIS method (enabled
   * only, in cascade order). Empty = no fee box for this tender; always empty
   * for GC/Coin.
   */
  fees: PosCustomFeeTypeOption[];
};

export async function getPosPaymentMethods(): Promise<PosPaymentMethodOption[]> {
  const { data } = await api.get<{ methods: PosPaymentMethodOption[] }>('/pos/payment-methods');
  return data.methods;
}

/**
 * One admin-configured custom fee type, as offered at the till. Fees ride on
 * each payment method (`PosPaymentMethodOption.fees`); the old standalone
 * `/pos/custom-fee-types` list is no longer read by this bundle.
 *
 * `calc` says where the amount comes from: `manual` is the cashier typing it
 * (a delivery fee varies by distance), while `percent`/`flat` are priced from
 * the registry and shown read-only. The till prices them only to SHOW the
 * cashier — the server recomputes the cascade at checkout and rejects any
 * amount sent for a computed type.
 */
export type PosCustomFeeTypeOption = {
  code: string;
  label: string;
  calc: PosCustomFeeCalc;
};

/**
 * LMS: how this tenant wants a course product handled at the till. `warn` is
 * the server's default. The server enforces `block` on its own — the till only
 * uses this to say so before the cashier charges.
 */
export type PosCourseSaleMode = 'allow' | 'warn' | 'block';

export async function getPosCoursePolicy(): Promise<PosCourseSaleMode> {
  const { data } = await api.get<{ mode: PosCourseSaleMode }>('/pos/course-policy');
  return data.mode;
}

export type PosCheckoutPayload = {
  branchId: string;
  buyerUserId: string;
  buyerSlotId?: string | null;
  items: Array<{ productId: string; variantId?: string; qty: number }>;
  payment: {
    method: 'cash' | 'card' | 'wallet';
    amountTendered: string;
    // BF4: pay in cash (default) or debit the buyer's gc/coin wallet balance.
    currency?: 'cash' | 'gc' | 'coin';
    // F1 CMS: the `pos_payment_methods` registry code chosen at the tender step.
    paymentMethodCode?: string;
    idempotencyKey?: string;
  };
  notes?: string;
  // QoL Slice 5b — optional order-level cashier discount. When pct/amount
  // exceed the tenant cap, `approvalChip` (from /pos/discount-override/verify)
  // is required.
  managerDiscount?: {
    pct?: number;
    amount?: number;
    reason?: string;
    approvalChip?: string;
  };
  /** The preset pressed, or the code typed. One or the other, never both. */
  discountPresetId?: string;
  couponCode?: string;
  /** A gift check of the buyer's, instead of either of the above. */
  voucherId?: string;
  /** Manager approval for a preset marked "needs a manager PIN". */
  couponApprovalChip?: string;
  // Ad-hoc charge typed at the till. Added on top of the discounted goods,
  // never discounted, never PV-bearing. Cash-denominated only.
  /** DEPRECATED single ad-hoc fee. Kept so an in-flight bundle keeps working. */
  customFee?: { label: string; amount: string };
  /** Registry-backed fees: the server resolves each label from `typeCode`. */
  /** `amount` only for a MANUAL type — the server prices the rest itself. */
  customFees?: Array<{ typeCode: string; amount?: string }>;
  // "Sell to Stockist" mode: the sale is a stock transfer into the buyer slot's
  // stockist branch (server validates the slot owns one). Default false.
  stockistTransfer?: boolean;
};

/**
 * Cashier discount config the Review modal reads to decide whether to show
 * the discount section and how to apply the cap. Per-branch (not tenant),
 * sourced from the active Branch row in `listBranches()`.
 */
export type CashierDiscountConfig = {
  /** `off`, `free` (typed discount within the caps) or `presets_only`. */
  mode: CashierDiscountMode;
  /** A discount can be typed at all. It may still need a manager PIN. */
  enabled: boolean;
  max_pct_without_approval: number;
  max_amount_without_approval: number;
};

export function cashierDiscountFromBranch(b: Branch | null | undefined): CashierDiscountConfig {
  const mode = resolveCashierDiscountMode(b);
  return {
    mode,
    enabled: mode !== 'off',
    max_pct_without_approval: Number(b?.cashierDiscountMaxPct ?? 0),
    max_amount_without_approval: Number(b?.cashierDiscountMaxAmount ?? 0),
  };
}

/** What the till may offer as a discount at a branch (`GET /pos/discount-presets`). */
export type PosDiscountOffer = {
  mode: CashierDiscountMode;
  /** The preset buttons. Empty when the branch's mode is `off`. */
  presets: PosDiscountPreset[];
  /** Some typed code is good at the till, so the code box is worth showing. */
  codesEnabled: boolean;
};

export async function getPosDiscountOffer(branchId: string): Promise<PosDiscountOffer> {
  const res = await api.get<PosDiscountOffer>('/pos/discount-presets', { params: { branchId } });
  return res.data;
}

/** One of the buyer's gift checks the till can apply (cashier phase 4). */
export type PosVoucher = {
  id: string;
  code: string;
  programId: string;
  programName: string;
  description: string | null;
  discountType: 'percent' | 'amount';
  discountValue: string;
  discountScope: 'one_unit' | 'every_unit';
  appliesToAll: boolean;
  productIds: string[];
  productNames: string[];
  expiresAt: string | null;
};

/** The buyer's usable gift checks; empty while the tenant has them off. */
export async function getPosVouchers(
  buyerUserId: string,
): Promise<{ label: string; vouchers: PosVoucher[] }> {
  const res = await api.get<{ label: string; vouchers: PosVoucher[] }>('/pos/vouchers', {
    params: { buyerUserId },
  });
  return res.data;
}

/**
 * A gift check code typed or scanned at the till, checked against the buyer.
 * Rejects with the API's reason (`voucher_invalid` when it is no gift check at
 * all, so the caller can try it as a discount code instead).
 */
export async function resolvePosVoucher(code: string, buyerUserId: string): Promise<PosVoucher> {
  const res = await api.get<PosVoucher>('/pos/vouchers/resolve', {
    params: { code, buyerUserId },
  });
  return res.data;
}

/**
 * QoL Slice 5b — verify a manager PIN. Returns a short-lived signed approval
 * chip the cashier passes alongside an above-cap manager discount at
 * /pos/checkout.
 */
export async function verifyManagerPin(args: {
  managerEmail: string;
  pin: string;
}): Promise<{ approvalChip: string; managerId: string; expiresInSeconds: number }> {
  const res = await api.post<{ approvalChip: string; managerId: string; expiresInSeconds: number }>(
    '/pos/discount-override/verify',
    args,
  );
  return res.data;
}

export type ReleaseHistoryRow = {
  orderId: string;
  orderCode: string;
  orderTotal: string;
  releasedAt: string;
  cashierId: string | null;
  cashierEmail: string | null;
  buyerUserId: string;
  branchId: string | null;
  branchCode: string | null;
};

export async function listReleases(
  params: {
    cashierId?: string;
    memberUserId?: string;
    from?: string;
    to?: string;
    limit?: number;
  } = {},
): Promise<ReleaseHistoryRow[]> {
  const res = await api.get<ReleaseHistoryRow[]>('/pos/releases', { params });
  return res.data;
}

/**
 * One sold line, as printed on the receipt tape. Returned identically by
 * `POST /pos/checkout` and `GET /pos/orders/:id` so the checkout tape and the
 * release/reprint tape can never drift.
 */
export type PosCheckoutResult = {
  order: {
    id: string;
    code: string;
    // BF3: 'completed' (auto mode) or 'to_release' (manual — claim at Releasing).
    status: string;
    subtotal: string;
    discountTotal: string;
    /** The coupon / preset and the cashier's typed discount, for the tape. */
    couponCode?: string | null;
    couponTotal?: string;
    couponLabel?: string | null;
    managerDiscountAmount?: string;
    total: string;
    totalPv: string;
    customFeeLabel: string | null;
    customFeeAmount: string;
    /** Per-fee breakdown; `[]` on orders placed before it existed. */
    customFees?: Array<{ code: string | null; label: string; amount: string }> | null;
    branchId: string;
    buyerUserId: string;
    buyerSlotId: string | null;
    notes: string | null;
    createdAt: string;
  };
  payment: {
    id: string;
    /** Coarse tender bucket. `methodLabel` is what the tape should print. */
    method: string;
    methodLabel: string | null;
    amount: string;
    amountTendered: string;
    changeDue: string;
    status: string;
  };
  // BF3: minted at checkout in auto mode; empty in manual mode (minted at release).
  kitCodes: KitCode[];
  /** Sold lines for the tape. Server-provided so the receipt needs no extra fetch. */
  items: PosReceiptItem[];
  /** Who the sale was rung up for / by. Server-resolved — see PosCheckoutResult in the API. */
  buyerName: string | null;
  cashierName: string | null;
  replayed: boolean;
};

export async function posCheckout(payload: PosCheckoutPayload): Promise<PosCheckoutResult> {
  const res = await api.post<PosCheckoutResult>('/pos/checkout', payload);
  return res.data;
}

// ── BF3: releasing area ──────────────────────────────────────────────

export type ReleasingOrder = {
  id: string;
  code: string;
  total: string;
  createdAt: string;
  buyerUserId: string;
  buyerEmail: string;
  buyerName: string | null;
  paymentMethod: string | null;
  /** The tender the cashier picked ("BPI Bank"). Show THIS, not `paymentMethod`. */
  paymentMethodLabel: string | null;
  paymentAmount: string | null;
};

export async function listReleasing(branchId: string): Promise<ReleasingOrder[]> {
  const res = await api.get<ReleasingOrder[]>('/pos/releasing', { params: { branchId } });
  return res.data;
}

export type PosReleaseResult = {
  orderId: string;
  kitCodes: KitCode[];
  buyerSlotId: string | null;
  replayed: boolean;
};

export async function releaseOrder(orderId: string): Promise<PosReleaseResult> {
  const res = await api.post<PosReleaseResult>(`/pos/orders/${orderId}/release`);
  return res.data;
}

export type PosOrderDetail = {
  id: string;
  code: string;
  status: string;
  subtotal: string;
  discountTotal: string;
  couponCode?: string | null;
  couponTotal?: string;
  couponLabel?: string | null;
  managerDiscountAmount?: string;
  total: string;
  customFeeLabel: string | null;
  customFeeAmount: string;
  /** Per-fee breakdown; `[]` on orders placed before it existed. */
  customFees?: Array<{ code: string | null; label: string; amount: string }> | null;
  branchId: string | null;
  createdAt: string;
  releasedAt: string | null;
  notes: string | null;
  /** Amendment stamp for `notes` — null when the remark was never edited. */
  notesUpdatedAt: string | null;
  notesUpdatedByName: string | null;
  buyerName: string | null;
  buyerEmail: string | null;
  /** Till operator on the ORIGINAL payment (payments are oldest-first). */
  cashierName: string | null;
  payments: Array<{
    id: string;
    /** Coarse tender bucket. `methodLabel` is what a human should read. */
    method: string;
    methodLabel: string | null;
    amount: string;
    amountTendered: string;
    changeDue: string;
    status: string;
  }>;
  items: PosReceiptItem[];
  branch: {
    name: string;
    code: string;
    completionMode: string;
    receiptHeader: ReceiptHeader;
    receiptFields: Partial<ReceiptFieldFlags>;
    receiptPaperWidth?: number;
  } | null;
  kitCodes: Array<KitCode & { redeemedAt: string | null }>;
  /** True iff no kit code from this order has been redeemed (server-computed). */
  allRefundable?: boolean;
  /** True iff a completed order may be voided (completed + nothing redeemed). */
  voidable?: boolean;
  /** Reasons captured at a void / refund. Never surfaced anywhere before. */
  reasonHistory: Array<{
    id: string;
    status: string;
    note: string | null;
    changedAt: string;
    changedByName: string | null;
  }>;
};

/** Amend a POS sale's remarks. Requires `pos.memo.edit`. */
export async function updateOrderMemo(orderId: string, notes: string | null) {
  return (await api.patch(`/pos/orders/${orderId}/memo`, { notes })).data;
}

/** Amend the reason captured at a void / refund. Requires `pos.memo.edit`. */
export async function updateOrderReason(orderId: string, historyId: string, note: string) {
  return (await api.patch(`/pos/orders/${orderId}/history/${historyId}/note`, { note })).data;
}

/** Amend an end-of-shift note. Requires `pos.memo.edit`. */
export async function updateShiftCloseNotes(id: string, notes: string | null) {
  return (await api.patch(`/pos/shift-closes/${id}/notes`, { notes })).data;
}

export async function getPosOrder(orderId: string): Promise<PosOrderDetail> {
  const res = await api.get<PosOrderDetail>(`/pos/orders/${orderId}`);
  return res.data;
}

/**
 * Void a POS sale. `damaged` (D28) restocks the units and then writes them off
 * in the same transaction — net stock unchanged, the loss booked as a write-off.
 * OPTIONAL on purpose: the releasing screen voids un-released goods that never
 * left the counter, so it stays a plain restock.
 */
export async function voidOrder(
  orderId: string,
  reason: string,
  damaged = false,
): Promise<{ paymentVoided: boolean; writeOffRef: string | null }> {
  const res = await api.post<{ paymentVoided: boolean; writeOffRef: string | null }>(
    `/pos/orders/${orderId}/void`,
    { reason, damaged },
  );
  return res.data;
}

// ── FS Activation via Cashier ────────────────────────────────────────

/** An FS-eligible membership tier (admin-flagged) the cashier may hand out. */
export type FsMembership = { id: string; code: string; name: string };

export async function listFsMemberships(): Promise<FsMembership[]> {
  const res = await api.get<FsMembership[]>('/pos/fs-memberships');
  return res.data;
}

export type ActivateFsResult = {
  slotId: string;
  slotCode: string;
  sponsorSlotId: string;
  binaryParentSlotId: string | null;
  binaryPosition: 'L' | 'R' | null;
  membershipId: string;
  membershipCode: string;
};

/** Directly create + activate a FREE (FS) slot for a member — no order/payment. */
export async function activateFsSlot(payload: {
  memberId: string;
  membershipId: string;
  sponsorCode?: string;
  anchorSlotId?: string;
  side?: 'L' | 'R';
}): Promise<ActivateFsResult> {
  const res = await api.post<ActivateFsResult>('/pos/slots/activate-fs', payload);
  return res.data;
}

// ── Cashier area: transactions, daily report, end-of-shift ───────────

export type PosTransaction = {
  orderId: string;
  code: string;
  createdAt: string;
  status: string;
  total: string;
  /** Coupon / preset plus the cashier's typed discount. Optional: an API older
   *  than till coupons does not send it. */
  tillDiscount?: string;
  tillDiscountLabel?: string | null;
  paymentCurrency: string;
  /** Coarse tender bucket — for logic (e.g. the cash-sales tile), not display. */
  paymentMethod: string | null;
  /** The tender the cashier picked ("BPI Bank"). Show THIS in a Payment column. */
  paymentMethodLabel: string | null;
  paymentAmount: string | null;
  changeDue: string | null;
  cashierId: string | null;
  cashierEmail: string | null;
  /** Who bought. Server-resolved, falling back to the buyer's email, so it is
   *  never blank for a member who never set a name. */
  buyerName: string | null;
  itemCount: number;
  /** Whether the sale carries a remark (the text itself is on the detail). */
  hasNotes: boolean;
};

export async function listTransactions(params: {
  branchId: string;
  status?: string;
  cashierId?: string;
  from?: string;
  to?: string;
  q?: string;
  /** F1 CMS registry code — filter by the tender the cashier actually picked. */
  paymentMethod?: string;
  limit?: number;
}): Promise<PosTransaction[]> {
  const res = await api.get<PosTransaction[]>('/pos/transactions', { params });
  return res.data;
}

export type PosTransactionsSummary = {
  totals: Array<{
    currency: string;
    count: number;
    total: string;
    completedCount: number;
    completedTotal: string;
  }>;
  byMethod: Array<{ code: string | null; label: string; count: number; total: string }>;
};

/**
 * Totals over EVERY row matching the same filters as `listTransactions` — the
 * list is capped at 500 rows, so summing it client-side would silently
 * understate a busy range. Same query params minus `limit`.
 */
export async function getTransactionsSummary(params: {
  branchId: string;
  status?: string;
  cashierId?: string;
  from?: string;
  to?: string;
  q?: string;
  paymentMethod?: string;
}): Promise<PosTransactionsSummary> {
  const res = await api.get<PosTransactionsSummary>('/pos/transactions/summary', { params });
  return res.data;
}

// ── Orders tab (branch-scoped port of the admin Orders view) ─────────────────
export type BranchOrder = {
  orderId: string;
  code: string;
  type: string;
  status: string;
  total: string;
  totalPv: string;
  paymentCurrency: string;
  buyerName: string | null;
  buyerEmail: string | null;
  slotCode: string | null;
  trackingNumber: string | null;
  itemCount: number;
  createdAt: string;
};

export type BranchOrderList = {
  rows: BranchOrder[];
  total: number;
  page: number;
  pageSize: number;
};

export async function listBranchOrders(params: {
  branchId: string;
  type?: string;
  status?: string;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}): Promise<BranchOrderList> {
  const res = await api.get<BranchOrderList>('/pos/orders', { params });
  return res.data;
}

export type BranchOrderDetail = {
  id: string;
  code: string;
  type: string;
  status: string;
  total: string;
  totalPv: string;
  shippingFee: string;
  paymentCurrency: string;
  courierId: string | null;
  trackingNumber: string | null;
  shipToAddress: Record<string, unknown> | null;
  buyerName: string | null;
  buyerEmail: string | null;
  branchId: string | null;
  createdAt: string;
  items: Array<{
    name: string;
    variantName: string | null;
    qty: number;
    unitPrice: string;
    lineTotal: string;
  }>;
  branch: { name: string; code: string; completionMode: string; receiptHeader: unknown } | null;
  fulfillEdges: Array<{ from: string; to: string; requires?: string }>;
};

export async function getBranchOrder(orderId: string): Promise<BranchOrderDetail> {
  const res = await api.get<BranchOrderDetail>(`/pos/orders/${orderId}/detail`);
  return res.data;
}

export async function fulfillBranchOrder(
  orderId: string,
  payload: { status: string; note?: string; trackingNumber?: string },
): Promise<{ status: string }> {
  const res = await api.post<{ status: string }>(`/pos/orders/${orderId}/fulfill`, payload);
  return res.data;
}

export type DailySalesReport = {
  branchId: string;
  from: string;
  to: string;
  ordersCount: number;
  grossSales: string;
  pvTotal: string;
  refundsCount: number;
  refundsTotal: string;
  paymentBreakdown: Array<{ method: string; count: number; total: string }>;
  topItems: Array<{
    productId: string;
    sku: string;
    name: string;
    variantName: string | null;
    qty: number;
    total: string;
  }>;
  /**
   * What was taken off the day's sales, by kind (`grossSales` is already net of
   * it). Optional: an API older than till coupons does not send it.
   */
  discounts?: {
    memberPricing: string;
    coupons: string;
    cashier: string;
    byLabel: Array<{ label: string; count: number; total: string }>;
  };
  /**
   * Wallet top-ups loaded at this branch's tills (cashier phase 5), by method.
   * Money taken, not sales. Optional: an older API does not send it.
   */
  walletTopups?: Array<{
    method: string;
    label: string;
    tenderKind: 'cash' | 'exact';
    count: number;
    total: string;
  }>;
};

export async function getDailyReport(params: {
  branchId: string;
  from: string;
  to: string;
}): Promise<DailySalesReport> {
  const res = await api.get<DailySalesReport>('/pos/daily-report', { params });
  return res.data;
}

export type ShiftCloseResult = {
  record: {
    id: string;
    cashierUserId: string;
    branchId: string | null;
    startAt: string;
    endAt: string;
    expectedCash: string;
    actualCash: string;
    variance: string;
    notes: string | null;
  };
  expectedCash: string;
  variance: string;
  /** Wallet top-ups taken in cash at this till in the shift, part of `expectedCash`. */
  topupCashCollected?: string;
};

export async function shiftClose(payload: {
  branchId: string;
  startAt: string;
  actualCash: number;
  notes?: string;
}): Promise<ShiftCloseResult> {
  const res = await api.post<ShiftCloseResult>('/pos/shift-close', payload);
  return res.data;
}

export type ShiftCloseRow = {
  id: string;
  cashierUserId: string;
  cashierEmail: string | null;
  branchId: string | null;
  startAt: string;
  endAt: string;
  expectedCash: string;
  actualCash: string;
  variance: string;
  notes: string | null;
  /** Amendment stamp — set when the note was edited after the shift closed. */
  notesUpdatedAt: string | null;
  notesUpdatedByName: string | null;
};

export async function listShiftCloses(
  params: {
    branchId?: string;
    cashierId?: string;
    limit?: number;
  } = {},
): Promise<ShiftCloseRow[]> {
  const res = await api.get<ShiftCloseRow[]>('/pos/shift-closes', { params });
  return res.data;
}

// ── Accounting v1: consignment at the till ──────────────────────────────────

/** Public branding/config endpoint (no auth) — only the fields the till needs. */
export async function getPublicTenantConfig(): Promise<{
  /** Tenant display name, for the rail's wordmark when there is no logo. */
  name: string;
  /** Theme tokens (Admin → Layout & Theme); `{}` for an un-themed tenant. */
  theme: ThemeTokens;
  consignmentEnabled: boolean;
  stockistConsignmentEnabled: boolean;
  /** Tenant's display name for the coin wallet (BF4 `coin_label`), e.g. "UPCoin". */
  coinLabel: string;
  /** Gift checks (cashier phase 4) are switched on. */
  vouchersEnabled: boolean;
  /** What the tenant calls a gift check, singular ("Gift Check"). */
  voucherLabel: string;
  /** Wallet top-up at the till (cashier phase 5) is switched on. */
  posTopupEnabled: boolean;
}> {
  const res = await api.get<{
    name?: string;
    theme?: ThemeTokens | null;
    consignmentEnabled?: boolean;
    stockistConsignmentEnabled?: boolean;
    coinLabel?: string;
    vouchersEnabled?: boolean;
    voucherLabel?: string;
    posTopupEnabled?: boolean;
  }>('/tenant/config');
  return {
    name: res.data.name?.trim() || 'Cashier POS',
    theme: res.data.theme ?? {},
    consignmentEnabled: res.data.consignmentEnabled === true,
    stockistConsignmentEnabled: res.data.stockistConsignmentEnabled === true,
    coinLabel: res.data.coinLabel?.trim() || DEFAULT_COIN_LABEL,
    vouchersEnabled: res.data.vouchersEnabled === true,
    voucherLabel: res.data.voucherLabel?.trim() || 'Gift Check',
    posTopupEnabled: res.data.posTopupEnabled === true,
  };
}

// ── Wallet top-up at the till (cashier phase 5) ─────────────────────────────

export type TopupMethod = { code: string; label: string; tenderKind: 'cash' | 'exact' };

/** The till's cash and exact methods a top-up may be paid with. */
export async function listTopupMethods(): Promise<TopupMethod[]> {
  const res = await api.get<TopupMethod[]>('/pos/topups/methods');
  return res.data;
}

/** What the till needs before taking a top-up (cashier phase 5). */
export type TopupConfig = {
  /** A top-up of this many pesos or more needs a manager PIN; null = never. */
  pinAbove: string | null;
  exactMethods: boolean;
};

export async function getTopupConfig(): Promise<TopupConfig> {
  const res = await api.get<TopupConfig>('/pos/topups/config');
  return res.data;
}

/** A manager approves one large top-up: for THIS slot and THIS amount only. */
export async function approveTopup(body: {
  managerEmail: string;
  pin: string;
  slotId: string;
  amount: string;
}): Promise<{ approvalChip: string }> {
  const res = await api.post<{ approvalChip: string }>('/pos/topups/approve', body);
  return res.data;
}

export type TopupPreview = {
  bonuses: Array<{ amount: string; currency: 'cash' | 'gc' }>;
  vouchers: Array<{ programName: string }>;
};

/** What a top-up of `amount` earns: bracket bonuses and gift checks. */
export async function previewTopup(amount: string): Promise<TopupPreview> {
  const res = await api.post<TopupPreview>('/pos/topups/preview', { amount });
  return res.data;
}

export type CounterTopup = {
  topupId: string;
  /** The same key was used before: nothing new was credited. */
  replayed: boolean;
  slotId: string;
  slotCode: string;
  userId: string;
  memberName: string;
  branchName: string;
  principal: string;
  method: string;
  methodLabel: string;
  tenderKind: 'cash' | 'exact';
  reference: string | null;
  bonuses: Array<{ amount: string; currency: 'cash' | 'gc' }>;
  vouchers: Array<{ id: string; code: string; programName: string; expiresAt: string | null }>;
  createdAt: string;
};

export async function createTopup(body: {
  branchId: string;
  userId: string;
  slotId: string;
  amount: string;
  paymentMethodCode: string;
  reference?: string | null;
  idempotencyKey: string;
  approvalChip?: string | null;
}): Promise<CounterTopup> {
  const res = await api.post<CounterTopup>('/pos/topups', body);
  return res.data;
}

/** Public tenant brand-logo URL — the API 302-redirects to a presigned object URL. */
export function tenantLogoUrl(which: 'light' | 'dark' | 'favicon'): string {
  return `${BASE_URL}/tenant/logo/${which}`;
}

/** Public catalog image URL — the API 302-redirects to a presigned object URL. */
export function productImageUrl(productId: string, idx: number): string {
  return `${BASE_URL}/products/${productId}/image/${idx}`;
}

export type ConsignmentLineInput = {
  productId: string;
  variantId?: string | null;
  qty: number;
  unitPrice: string;
};

export type ConsignmentCustomerRow = {
  customer_user_id: string;
  customer_name: string;
  email: string;
  units: number;
  open_billings: number;
  receivable: string;
  last_activity: string | null;
};

export async function listConsignmentCustomers(q?: string): Promise<ConsignmentCustomerRow[]> {
  const res = await api.get<{ rows: ConsignmentCustomerRow[] }>('/pos/consignment-customers', {
    params: q ? { q } : {},
  });
  return res.data.rows;
}

export type ConsignmentHolding = {
  productId: string;
  productName: string;
  variantId: string | null;
  variantName: string | null;
  qty: number;
  updatedAt: string;
};

export type ConsignmentInvoiceSummary = {
  id: string;
  docNo: string;
  customerUserId: string;
  consignmentId: string | null;
  status: 'open' | 'partial' | 'paid' | 'void';
  invoiceDate: string;
  dueDate: string | null;
  subtotal: string;
  total: string;
  amountPaid: string;
  memo: string | null;
  createdAt: string;
};

export type ConsignmentDocRow = {
  doc_type: 'consignment' | 'billing' | 'return' | 'payment';
  doc_no: string;
  status: string;
  amount: string | null;
  memo: string | null;
  id: string;
  sort_at?: string;
};

export type ConsignmentCustomerDetail = {
  customer: {
    id: string;
    fullName: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string;
    phone: string | null;
  };
  holdings: ConsignmentHolding[];
  invoices: ConsignmentInvoiceSummary[];
  docs: ConsignmentDocRow[];
  /** Money this customer has already paid that is not against any billing. */
  credit: string;
};

export async function getConsignmentCustomer(userId: string): Promise<ConsignmentCustomerDetail> {
  const res = await api.get<ConsignmentCustomerDetail>(`/pos/consignment-customers/${userId}`);
  return res.data;
}

export type ConsignmentHeader = {
  id: string;
  docNo: string;
  customerUserId: string;
  status: 'posted' | 'void';
  memo: string | null;
  createdAt: string;
};

export async function createConsignment(input: {
  customerUserId: string;
  branchId?: string;
  lines: ConsignmentLineInput[];
  memo?: string | null;
}): Promise<ConsignmentHeader> {
  const res = await api.post<ConsignmentHeader>('/pos/consignments', input);
  return res.data;
}

export type ConsignmentDetail = ConsignmentHeader & {
  customerName: string;
  branchId: string;
  branchName: string;
  voidReason: string | null;
  lines: Array<{
    productId: string;
    productName: string;
    variantId: string | null;
    variantName: string | null;
    qty: number;
    unitPrice: string;
  }>;
};

export async function getConsignment(id: string): Promise<ConsignmentDetail> {
  const res = await api.get<ConsignmentDetail>(`/pos/consignments/${id}`);
  return res.data;
}

export async function voidConsignment(id: string, reason: string): Promise<void> {
  await api.post(`/pos/consignments/${id}/void`, { reason });
}

export async function createConsignmentInvoice(input: {
  customerUserId: string;
  consignmentId?: string | null;
  lines: ConsignmentLineInput[];
  memo?: string | null;
}): Promise<ConsignmentInvoiceSummary> {
  const res = await api.post<ConsignmentInvoiceSummary>('/pos/consignment-invoices', input);
  return res.data;
}

export type ConsignmentInvoiceDetail = ConsignmentInvoiceSummary & {
  customerName: string;
  voidReason: string | null;
  lines: Array<{
    productId: string;
    productName: string;
    variantId: string | null;
    variantName: string | null;
    qty: number;
    unitPrice: string;
    lineTotal: string;
  }>;
  payments: Array<{
    receiptId: string;
    docNo: string;
    amount: string;
    method: string;
    receivedAt: string;
    status: 'posted' | 'void';
  }>;
};

export async function getConsignmentInvoice(id: string): Promise<ConsignmentInvoiceDetail> {
  const res = await api.get<ConsignmentInvoiceDetail>(`/pos/consignment-invoices/${id}`);
  return res.data;
}

export async function voidConsignmentInvoice(id: string, reason: string): Promise<void> {
  await api.post(`/pos/consignment-invoices/${id}/void`, { reason });
}

export async function createConsignmentReturn(input: {
  customerUserId: string;
  branchId?: string;
  lines: Array<{ productId: string; variantId?: string | null; qty: number }>;
  memo?: string | null;
}): Promise<{ id: string; docNo: string }> {
  const res = await api.post<{ id: string; docNo: string }>('/pos/consignment-returns', input);
  return res.data;
}

export async function voidConsignmentReturn(id: string, reason: string): Promise<void> {
  await api.post(`/pos/consignment-returns/${id}/void`, { reason });
}

export type ConsignmentReceiptResult = {
  receipt: {
    id: string;
    docNo: string;
    customerUserId: string;
    amount: string;
    method: string;
    reference: string | null;
    receivedAt: string;
    status: 'posted' | 'void';
  };
  replayed: boolean;
};

/**
 * Cash/Bank accounts a till may post a collection to. Deliberately balance-free
 * — a cashier picks an account, they do not audit one.
 */
export type PosFundsAccount = {
  id: string;
  name: string;
  kind: 'cash' | 'bank' | 'cheque' | 'ewallet';
  branchId: string | null;
};

export async function listPosFundsAccounts(
  branchId?: string | null,
): Promise<{ required: boolean; rows: PosFundsAccount[] }> {
  const res = await api.get<{ required: boolean; rows: PosFundsAccount[] }>('/pos/funds-accounts', {
    params: branchId ? { branchId } : {},
  });
  return res.data;
}

export async function createConsignmentReceipt(input: {
  customerUserId: string;
  /** Unscoped callers name the till branch (cash receipts count toward its shift). */
  branchId?: string;
  amount: string;
  method: string;
  reference?: string | null;
  allocations: Array<{ invoiceId: string; amount: string }>;
  memo?: string | null;
  clientRequestId?: string | null;
  /** Cash/Bank register: which account received the money. */
  fundsAccountId?: string | null;
}): Promise<ConsignmentReceiptResult> {
  const res = await api.post<ConsignmentReceiptResult>('/pos/consignment-receipts', input);
  return res.data;
}

export type ConsignmentReceiptDetail = ConsignmentReceiptResult['receipt'] & {
  fundsAccountName?: string | null;
  customerName: string;
  memo: string | null;
  voidReason: string | null;
  allocations: Array<{ invoiceId: string; invoiceDocNo: string; amount: string }>;
};

export async function getConsignmentReceipt(id: string): Promise<ConsignmentReceiptDetail> {
  const res = await api.get<ConsignmentReceiptDetail>(`/pos/consignment-receipts/${id}`);
  return res.data;
}

export async function voidConsignmentReceipt(id: string, reason: string): Promise<void> {
  await api.post(`/pos/consignment-receipts/${id}/void`, { reason });
}

/**
 * Spend credit the customer already has — money they handed over earlier that
 * was never applied to a billing. Same authority as taking a payment.
 */
export async function applyCustomerCredit(input: {
  customerUserId: string;
  allocations: Array<{ invoiceId: string; amount: string }>;
}): Promise<{ applied: string; creditLeft: string }> {
  const res = await api.post<{ applied: string; creditLeft: string }>(
    '/pos/ar/credit-applications',
    input,
  );
  return res.data;
}

export type SoaLine = {
  date: string;
  docType: string;
  docNo: string;
  ref: string | null;
  charges: string;
  payments: string;
  balance: string;
};

export type CustomerSoa = {
  customer: { id: string; name: string; email: string; phone: string | null };
  from: string | null;
  to: string | null;
  openingBalance: string;
  lines: SoaLine[];
  totalCharges: string;
  totalPayments: string;
  closingBalance: string;
};

export async function getCustomerSoa(
  userId: string,
  params: { from?: string; to?: string } = {},
): Promise<CustomerSoa> {
  const res = await api.get<CustomerSoa>(`/pos/consignment-customers/${userId}/soa`, { params });
  return res.data;
}

// ── Track 2A: stockist consignment at the till ──────────────────────────────
//
// The debtor is the BRANCH, not a person — every call is branch-keyed. Nothing
// here CREATES a billing: SAR- receivables are raised by the stockist's own POS
// sale (`bookStockistSellThrough`), which is the whole point of the feature.

export type StockistSummaryRow = {
  stockist_branch_id: string;
  name: string;
  code: string;
  units: number;
  open_billings: number;
  receivable: string;
  last_activity: string | null;
};

export async function listStockistSummary(q?: string): Promise<StockistSummaryRow[]> {
  const res = await api.get<{ rows: StockistSummaryRow[] }>('/pos/stockist-consignment-summary', {
    params: q ? { q } : {},
  });
  return res.data.rows;
}

export type StockistBranchRow = {
  id: string;
  name: string;
  code: string;
  ownerName?: string | null;
};

export async function listStockistBranches(q?: string): Promise<StockistBranchRow[]> {
  const res = await api.get<{ rows: StockistBranchRow[] }>('/pos/stockist-consignment-branches', {
    params: q ? { q } : {},
  });
  return res.data.rows;
}

export type StockistHoldingRow = {
  productId: string;
  variantId: string | null;
  productName: string;
  variantName?: string | null;
  sku?: string | null;
  qty: number;
};

export async function listStockistHoldings(
  stockistBranchId: string,
): Promise<StockistHoldingRow[]> {
  const res = await api.get<{ rows: StockistHoldingRow[] }>('/pos/stockist-consignment-holdings', {
    params: { stockistBranchId },
  });
  return res.data.rows;
}

export type StockistInvoiceRow = {
  id: string;
  docNo: string;
  status: 'open' | 'partial' | 'paid' | 'void';
  invoiceDate: string;
  total: string;
  amountPaid: string;
};

export async function listStockistInvoices(
  stockistBranchId: string,
  status?: string,
): Promise<StockistInvoiceRow[]> {
  const res = await api.get<{ rows: StockistInvoiceRow[] }>('/pos/stockist-consignment-invoices', {
    params: { stockistBranchId, ...(status ? { status } : {}) },
  });
  return res.data.rows;
}

export type StockistReceiptRow = {
  id: string;
  docNo: string;
  amount: string;
  method: string;
  reference: string | null;
  receivedAt: string;
  status: 'posted' | 'void';
};

export async function listStockistReceipts(
  stockistBranchId: string,
): Promise<StockistReceiptRow[]> {
  const res = await api.get<{ rows: StockistReceiptRow[] }>('/pos/stockist-consignment-receipts', {
    params: { stockistBranchId },
  });
  return res.data.rows;
}

export type StockistSoa = {
  branch: { id: string; name: string; code: string };
  openingBalance: string;
  totalCharges: string;
  totalPayments: string;
  closingBalance: string;
  lines: Array<{
    date: string;
    docType: string;
    docNo: string;
    ref: string | null;
    charges: string;
    payments: string;
    balance: string;
  }>;
};

export async function getStockistSoa(stockistBranchId: string): Promise<StockistSoa> {
  const res = await api.get<StockistSoa>(
    `/pos/stockist-consignment-branches/${stockistBranchId}/soa`,
  );
  return res.data;
}

export async function createStockistConsignment(input: {
  stockistBranchId: string;
  branchId?: string;
  lines: Array<{ productId: string; variantId?: string | null; qty: number; unitPrice: string }>;
  memo?: string | null;
  clientRequestId?: string;
}): Promise<{ id: string; docNo: string; replayed: boolean }> {
  const res = await api.post<{ id: string; docNo: string; replayed: boolean }>(
    '/pos/stockist-consignments',
    input,
  );
  return res.data;
}

export async function createStockistReturn(input: {
  stockistBranchId: string;
  branchId?: string;
  lines: Array<{ productId: string; variantId?: string | null; qty: number }>;
  memo?: string | null;
  clientRequestId?: string;
}): Promise<{ id: string; docNo: string; replayed: boolean }> {
  const res = await api.post<{ id: string; docNo: string; replayed: boolean }>(
    '/pos/stockist-consignment-returns',
    input,
  );
  return res.data;
}

export async function createStockistReceipt(input: {
  stockistBranchId: string;
  branchId?: string;
  amount: string;
  method: string;
  reference?: string | null;
  /** Omit to auto-apply oldest open billing first. */
  allocations?: Array<{ invoiceId: string; amount: string }>;
  memo?: string | null;
  fundsAccountId?: string | null;
  clientRequestId?: string;
}): Promise<{ id: string; docNo: string; replayed: boolean }> {
  const res = await api.post<{ id: string; docNo: string; replayed: boolean }>(
    '/pos/stockist-consignment-receipts',
    input,
  );
  return res.data;
}

/** All three void endpoints share one shape; only the collection differs. */
const STOCKIST_VOID_PATHS = {
  note: 'stockist-consignments',
  return: 'stockist-consignment-returns',
  receipt: 'stockist-consignment-receipts',
} as const;

export async function voidStockistDoc(
  kind: keyof typeof STOCKIST_VOID_PATHS,
  id: string,
  reason: string,
): Promise<{ docNo: string }> {
  const res = await api.post<{ docNo: string }>(`/pos/${STOCKIST_VOID_PATHS[kind]}/${id}/void`, {
    reason,
  });
  return res.data;
}
